import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { Input, Modal, SpinnerWithBackground, Text, Toggle } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { DataFeedColumnMappingFields } from './DataFeedColumnMappingFields'
import { DataFeedRecentRunsSection } from './DataFeedRecentRunsSection'
import {
	buildColumnMappingPayload,
	defaultColumnMappingFormValues,
	getColumnMappingFormValues,
} from './dataFeedColumnMapping'
import { useDataFeedConfigMutations } from './useDataFeedConfigMutations'

type RouterOutput = inferRouterOutputs<AppRouter>
type DataFeedConfig = NonNullable<RouterOutput['admin']['integrations']['dataFeed']['getByOrganization']>

interface UkgRaasDataFeedConfigModalProps {
	isOpen: boolean
	onClose: () => void
	organizationId: string
	existingConfig?: DataFeedConfig | null
}

const MASKED = '********'

export function UkgRaasDataFeedConfigModal({
	isOpen,
	onClose,
	organizationId,
	existingConfig,
}: UkgRaasDataFeedConfigModalProps) {
	const [formData, setFormData] = useState({
		name: '',
		enabled: true,
		customerApiKey: '',
		userApiKey: '',
		username: '',
		password: '',
		serviceUrl: '',
		reportPath: '',
		...defaultColumnMappingFormValues,
	})

	useEffect(() => {
		if (existingConfig) {
			const ukgConfig = existingConfig.adapterConfig as Record<string, unknown> | null
			setFormData({
				name: existingConfig.name || '',
				enabled: existingConfig.enabled ?? true,
				customerApiKey: ukgConfig?.customerApiKey === MASKED ? '' : (ukgConfig?.customerApiKey as string) || '',
				userApiKey: ukgConfig?.userApiKey === MASKED ? '' : (ukgConfig?.userApiKey as string) || '',
				username: (ukgConfig?.username as string) || '',
				password: ukgConfig?.password === MASKED ? '' : (ukgConfig?.password as string) || '',
				serviceUrl: (ukgConfig?.serviceUrl as string) || '',
				reportPath: (ukgConfig?.reportPath as string) || '',
				...getColumnMappingFormValues(existingConfig.columnMapping),
			})
		} else {
			setFormData({
				name: '',
				enabled: true,
				customerApiKey: '',
				userApiKey: '',
				username: '',
				password: '',
				serviceUrl: '',
				reportPath: '',
				...defaultColumnMappingFormValues,
			})
		}
	}, [existingConfig])

	const { runs, createMutation, updateMutation, deleteMutation } = useDataFeedConfigMutations({
		organizationId,
		adapterType: 'ukg_raas',
		existingConfigId: existingConfig?.id,
		label: 'UKG Pro data feed',
		onClose,
	})

	const submitForm = () => {
		const adapterConfig: Record<string, string> = {
			username: formData.username,
			serviceUrl: formData.serviceUrl,
			reportPath: formData.reportPath,
		}
		const columnMapping = buildColumnMappingPayload(formData)

		if (existingConfig) {
			if (formData.customerApiKey) adapterConfig.customerApiKey = formData.customerApiKey
			if (formData.userApiKey) adapterConfig.userApiKey = formData.userApiKey
			if (formData.password) adapterConfig.password = formData.password

			updateMutation.mutate({
				id: existingConfig.id,
				name: formData.name,
				enabled: formData.enabled,
				adapterConfig,
				columnMapping,
			})
		} else {
			createMutation.mutate({
				adapterType: 'ukg_raas',
				organizationId,
				name: formData.name,
				enabled: formData.enabled,
				adapterConfig: {
					customerApiKey: formData.customerApiKey,
					userApiKey: formData.userApiKey,
					username: formData.username,
					password: formData.password,
					serviceUrl: formData.serviceUrl,
					reportPath: formData.reportPath,
				},
				columnMapping,
			})
		}
	}

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		submitForm()
	}

	const handleDelete = () => {
		if (
			existingConfig &&
			window.confirm(
				'Delete this UKG Pro data feed configuration? This will also remove all run history and cannot be undone.',
			)
		) {
			deleteMutation.mutate({ id: existingConfig.id })
		}
	}

	const isLoading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending

	const t = (v: string) => v.trim().length > 0
	const isFormValid = existingConfig
		? t(formData.name) && t(formData.username) && t(formData.serviceUrl) && t(formData.reportPath)
		: t(formData.name) &&
			t(formData.customerApiKey) &&
			t(formData.userApiKey) &&
			t(formData.username) &&
			t(formData.password) &&
			t(formData.serviceUrl) &&
			t(formData.reportPath) &&
			t(formData.firstName) &&
			t(formData.lastName) &&
			t(formData.email) &&
			t(formData.externalId)

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title='Configure Data Feed (UKG Pro)'
			buttons={[
				{
					children: 'Cancel',
					onClick: onClose,
					variant: 'secondary',
					disabled: isLoading,
				},
				...(existingConfig
					? [
							{
								children: 'Delete',
								onClick: handleDelete,
								variant: 'destructive' as const,
								disabled: isLoading,
							},
						]
					: []),
				{
					children: existingConfig ? 'Update' : 'Save',
					onClick: submitForm,
					variant: 'primary',
					disabled: !isFormValid || isLoading,
				},
			]}>
			<form onSubmit={handleSubmit} className='space-y-4'>
				<Text variant='small' className='mb-4 text-neutral-600'>
					Configure your UKG Pro RaaS data feed. User data will be fetched from UKG Reports as a Service on an hourly
					schedule.
				</Text>

				<Input
					label='Name'
					value={formData.name}
					onChange={(e) => setFormData({ ...formData, name: e.target.value })}
					placeholder='e.g., UKG Pro User Feed'
					isRequired
				/>

				<div className='flex items-center gap-3'>
					<Toggle
						checked={formData.enabled}
						onCheckedChange={(checked) => setFormData({ ...formData, enabled: !!checked })}
					/>
					<Text variant='small'>Enabled</Text>
				</div>

				<Text variant='medium' weight='semibold' className='mt-6'>
					UKG Pro API Settings
				</Text>

				<Input
					label='Service URL'
					value={formData.serviceUrl}
					onChange={(e) => setFormData({ ...formData, serviceUrl: e.target.value })}
					placeholder='https://service4.ultipro.com'
					hint='The base URL for the UKG Pro API services'
					isRequired
				/>

				<Input
					label='Customer API Key'
					value={formData.customerApiKey}
					onChange={(e) => setFormData({ ...formData, customerApiKey: e.target.value })}
					placeholder={existingConfig ? '(unchanged — enter new value to update)' : 'Enter customer API key'}
					isRequired={!existingConfig}
				/>

				<Input
					label='User API Key'
					value={formData.userApiKey}
					onChange={(e) => setFormData({ ...formData, userApiKey: e.target.value })}
					placeholder={existingConfig ? '(unchanged — enter new value to update)' : 'Enter user API key'}
					isRequired={!existingConfig}
				/>

				<Input
					label='Username'
					value={formData.username}
					onChange={(e) => setFormData({ ...formData, username: e.target.value })}
					placeholder='api-service-account'
					isRequired
				/>

				<Input
					label='Password'
					type='password'
					value={formData.password}
					onChange={(e) => setFormData({ ...formData, password: e.target.value })}
					placeholder={existingConfig ? '(unchanged — enter new value to update)' : 'Enter password'}
					isRequired={!existingConfig}
				/>

				<Input
					label='Report Path'
					value={formData.reportPath}
					onChange={(e) => setFormData({ ...formData, reportPath: e.target.value })}
					placeholder='/content/folder/report_name'
					hint='The path to the RaaS report in UKG Pro'
					isRequired
				/>

				<DataFeedColumnMappingFields
					description='Map the report column names to the expected user fields.'
					values={formData}
					onChange={(patch) => setFormData({ ...formData, ...patch })}
				/>
			</form>

			{existingConfig && runs && <DataFeedRecentRunsSection runs={runs} />}

			{isLoading && <SpinnerWithBackground />}
		</Modal>
	)
}
